/**
 * Smart API: Manual Checkout feature.
 * (2026-04-24, Jason Cox) Extracted from the 2800-line unifiedApiService.ts
 * monolith. The SmartApiAdapter now delegates to these free functions so
 * each feature owns its own types + implementation in one small file.
 *
 * Endpoints:
 *   POST /scm/v1/rfid/lookupTags
 *   POST /scm/v1/rfid/manualCheckout
 *   POST /scm/v1/rfid/manualCheckoutHistory
 */
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import type { ApiConfig } from '../apiConfig';

// (2026-04-16, Jason Cox) Manual Checkout types.
export interface ManualCheckoutTagInfo {
  rfid: string;
  status: number | null;
  statusName: string;
  sku?: string;
  itemName?: string;
  // (2026-04-20, Jason Cox) smartboxId is used by the UI to scope the customer
  // dropdown and to detect mixed-smartbox batches (which block checkout).
  smartboxId?: number;
  smartboxName?: string;
  workcenterName?: string;
  clientName?: string;
  canCheckout: boolean;
  canCheckin: boolean;
  packageQty?: number;
}

// (2026-04-17 / 2026-04-24, Jason Cox) Picker name & company are NOT sent --
// the backend derives them from the authenticated session. Associated Party
// is a free-text label going forward (was a dropdown + customerId in 2026-04-21
// design; the id is no longer sent).
export interface ManualCheckoutPayload {
  rfids: string[];
  associatedParty: string;  // free-text party label; recorded in ticket note blob
  foreman?: string;         // customer foreman or approver
  location?: string;        // free-text site / lease / well / property name
  // (2026-06-11, Jason Cox) Picker identity is now free-text from the form
  // because deployments share one login. When sent, the backend uses these
  // instead of the session-derived name/company; when omitted it falls back to
  // the session (backward compat with older builds).
  pickerName?: string;      // who physically picked up the tags
  pickerCompany?: string;   // the picker's company
  // (2026-06-11, Jason Cox) `signature` superseded the typed `initials` field:
  // a base64 PNG data URL of a drawn signature. The backend uploads it to S3
  // (keyed by docNum) and stores only a marker in the note blob. `initials` is
  // retained as optional for backward compatibility but is no longer sent by
  // the PWA UI.
  signature?: string;       // base64 PNG data URL (e.g. "data:image/png;base64,...")
  initials?: string;        // DEPRECATED: picker's initials / typed signature
}

export interface ManualCheckoutResult {
  processed: { rfid: string; docNum: string; headId: number }[];
  errors: { rfid: string; error: string }[];
}

export interface ManualCheckoutHistoryOptions {
  page?: number;
  pageSize?: number;
  rfid?: string;
  sku?: string;
  customerId?: number;
  startTime?: string;
  endTime?: string;
}

// (2026-04-17 / 2026-04-21, Jason Cox) History row shape. associatedParty /
// foreman / pickerCompany / initials are parsed server-side from the
// md_rfid.note labeled-lines blob.
export interface ManualCheckoutHistoryRow {
  docNum: string;
  rfid: string;
  sku?: string;
  itemName?: string;
  clientName?: string;
  /** Dropdown selection at checkout time (customer or vendor), display name. */
  associatedParty?: string;
  foreman?: string;
  location?: string;
  pickerName?: string;
  pickerCompany?: string;
  initials?: string;
  // (2026-06-11, Jason Cox) Public S3 URL of the drawn signature image, rebuilt
  // server-side from the ticket docNum when the note carries the signature
  // marker. Empty/absent for legacy tickets that stored typed `initials`.
  signatureUrl?: string;
  docDate: string;
  docTime: string;
  checkoutBy?: string;
  currentStatusName: string;
  isCheckedOut: boolean;
  ticketType: 'checkout' | 'checkin';
}

export interface ManualCheckoutHistoryResult {
  data: ManualCheckoutHistoryRow[];
  total: number;
  currentPage: number;
  totalPages: number;
}

export async function lookupCheckoutTags(
  config: ApiConfig,
  rfids: string[],
): Promise<ManualCheckoutTagInfo[]> {
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/rfid/lookupTags`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify({ rfids }),
    },
    { maxRetries: 1, timeout: 10000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'lookupTags failed');
  return (data.data || []) as ManualCheckoutTagInfo[];
}

export async function submitManualCheckout(
  config: ApiConfig,
  payload: ManualCheckoutPayload,
): Promise<ManualCheckoutResult> {
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/rfid/manualCheckout`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(payload),
    },
    { maxRetries: 1, timeout: 15000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'manualCheckout failed');
  return data.data as ManualCheckoutResult;
}

export async function getManualCheckoutHistory(
  config: ApiConfig,
  options: ManualCheckoutHistoryOptions = {},
): Promise<ManualCheckoutHistoryResult> {
  const body = {
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 25,
    rfid: options.rfid ?? '',
    sku: options.sku ?? '',
    customerId: options.customerId,
    startTime: options.startTime ?? '',
    endTime: options.endTime ?? '',
  };
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/rfid/manualCheckoutHistory`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(body),
    },
    { maxRetries: 1, timeout: 15000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'manualCheckoutHistory failed');
  const payload = data.data || {};
  return {
    data: payload.data || [],
    total: payload.total || 0,
    currentPage: payload.current_page || body.page,
    totalPages: payload.last_page || 1,
  };
}
